import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Image as ImageIcon, DownloadSimple, Trash, SpinnerGap, ArrowsClockwise } from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import VisualGenerator, { downloadDataUrl } from "@/components/VisualGenerator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { useWorkspace } from "@/context/WorkspaceContext";

export default function Visuals() {
  const { activeId } = useWorkspace();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [varyingId, setVaryingId] = useState(null);

  const load = useCallback(async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/images?workspace_id=${activeId}`);
      setGallery(data);
    } finally {
      setLoading(false);
    }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/images/${id}`);
      setGallery((g) => g.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const vary = async (id) => {
    setVaryingId(id);
    try {
      await api.post(`/images/${id}/variations`, { count: 2 });
      await load();
      toast.success("Variations added to your library");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create variations");
    } finally {
      setVaryingId(null);
    }
  };

  return (
    <AppLayout>
      <div className="mb-8">
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">AI Visuals</h1>
        <p className="text-muted-foreground mt-2">Generate on-brand images for your posts with Gemini or GPT Image.</p>
      </div>

      <div className="rounded-lg border border-border bg-card p-6 md:p-8 mb-10">
        <VisualGenerator onGenerated={load} />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <ImageIcon size={18} className="text-muted-foreground" />
        <h2 className="text-lg font-heading font-bold">Your image library</h2>
        <Badge variant="secondary" data-testid="visuals-count">{gallery.length}</Badge>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><SpinnerGap size={28} className="animate-spin text-primary" /></div>
      ) : gallery.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-16 text-center" data-testid="visuals-empty">
          <ImageIcon size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="font-heading font-bold text-lg">No visuals yet</p>
          <p className="text-muted-foreground mt-1">Generate your first image above — it'll be saved here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="visuals-gallery">
          {gallery.map((img, i) => (
            <motion.div
              key={img.id}
              data-testid={`gallery-item-${img.id}`}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
              className="group relative rounded-lg overflow-hidden border border-border bg-card"
            >
              <img src={img.url || img.data_url} alt={img.prompt} className="w-full aspect-square object-cover" />
              {varyingId === img.id && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <SpinnerGap size={22} className="animate-spin text-white" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button size="icon" variant="secondary" data-testid={`gallery-download-${img.id}`}
                  onClick={() => downloadDataUrl(img.url || img.data_url)} title="Download">
                  <DownloadSimple size={16} />
                </Button>
                <Button size="icon" variant="secondary" data-testid={`gallery-vary-${img.id}`}
                  onClick={() => vary(img.id)} disabled={varyingId === img.id} title="Make variations">
                  <ArrowsClockwise size={16} />
                </Button>
                <Button size="icon" variant="destructive" data-testid={`gallery-delete-${img.id}`}
                  onClick={() => remove(img.id)} title="Delete">
                  <Trash size={16} />
                </Button>
              </div>
              <div className="absolute bottom-1 left-1">
                <Badge variant="secondary" className="text-[10px] font-mono uppercase opacity-90">
                  {img.provider === "openai" ? "GPT" : "Gemini"}
                </Badge>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
